<script lang="ts">
	import { onMount, type Snippet } from 'svelte';
	import AbleyButton from './AbleyButton.svelte';
	import CloseButton from './CloseButton.svelte';
	import IconButton from './IconButton.svelte';
	import ProfileMenu from './ProfileMenu.svelte';
	import MenuButton from './MenuButton.svelte';
	import type { MenuItem } from './MenuButton.svelte';
	import SupportFormModal from './SupportFormModal.svelte';
	import type { Environment } from '#lib/types.js';

	import IconBurgerMenu from '#lib/assets/three-bars.svg?raw';

	type NavLink = {
		label: string;
		href?: string;
		action?: () => void;
		disabled?: boolean;
		/** Short status pill after the label, e.g. "Alpha"; shown in capitals. */
		badge?: string;
	};

	type MenuButtonItem = {
		title: string;
		items: MenuItem[];
	};

	export type HeaderItem = NavLink | MenuButtonItem;

	interface HeaderProps {
		logo?: Snippet;
		items: (HeaderItem | undefined)[];
		environment?: Environment;
		/** Opens the access-key view from the profile menu. */
		onaccesskey: () => void;
	}

	const { logo, onaccesskey, items = [], environment = 'production' }: HeaderProps = $props();

	let showMenu = $state(false);
	let bannerDismissed = $state(false);
	const showBanner = $derived(!bannerDismissed && environment !== 'production');

	onMount(() => {
		const timer = setTimeout(() => (bannerDismissed = true), 60000);
		return () => clearTimeout(timer);
	});

	let supportOpen = $state(false);
	let supportWarm = $state(false);

	function handleMenuAction(action: string) {
		if (action === 'contact-support') supportOpen = true;
	}

	function handleMenuActionHover(action: string) {
		// Hover/focus of "Get support" starts loading the embed; it stays warm for the session.
		if (action === 'contact-support') supportWarm = true;
	}
</script>

{#snippet linkLabel(item: NavLink)}
	{item.label}{#if item.badge}<span class="badge">{item.badge}</span>{/if}
{/snippet}

<header class="header">
	<div class="bar">
		<nav aria-label="Main">
			<ul>
				<li class="menuIcon">
					<IconButton
						variant="quiet"
						round={false}
						onclick={() => (showMenu = !showMenu)}
						aria-label="Show menu"
						aria-expanded={showMenu}
						aria-controls="mainNavItems">
						{@html IconBurgerMenu}
					</IconButton>
				</li>
				<li class="logoContainer">
					{#if logo}{@render logo()}{/if}
				</li>
			</ul>
			<ul class="navItems" id="mainNavItems" class:active={showMenu}>
				{#each items as item, i (i)}
					{#if item !== undefined}
						<li>
							{#if 'title' in item}
								<MenuButton
									title={item.title}
									items={item.items}
									mobile={showMenu}
									onaction={handleMenuAction}
									onactionhover={handleMenuActionHover} />
							{:else if item.disabled}
								<AbleyButton variant="text" disabled>{@render linkLabel(item)}</AbleyButton>
							{:else if item.href}
								<AbleyButton variant="text" href={item.href} target="_blank">
									{@render linkLabel(item)}
								</AbleyButton>
							{:else if item.action}
								<AbleyButton variant="text" onclick={item.action}
									>{@render linkLabel(item)}</AbleyButton>
							{/if}
						</li>
					{/if}
				{/each}
				<li class="spacer"></li>
				<li>
					<ProfileMenu {onaccesskey} />
				</li>
			</ul>
		</nav>
	</div>
	{#if showBanner}
		<div class="banner" class:info={environment === 'demo' || environment === 'prototype'}>
			<p class="bannerMessage">
				{#if environment === 'demo' || environment === 'prototype'}
					This application is provided as a demonstration and is intended solely for this purpose,
					see
					<a href="https://abley.com/en-us/terms-of-use" target="_blank">
						Terms of Use<span class="screenReaderOnly"> (opens in new tab)</span></a
					>.
				{:else if environment === 'development'}
					Abley Spatial DEVELOPMENT environment
				{/if}
			</p>
			<CloseButton aria-label="Dismiss banner" onclick={() => (bannerDismissed = true)} />
		</div>
	{/if}
</header>

<SupportFormModal open={supportOpen} warm={supportWarm} onclose={() => (supportOpen = false)} />

<style>
	/* Superscript-style: lifted against the label rather than sitting beside it as a peer. */
	.badge {
		align-self: flex-start;
		/* The button spaces its children by --space-2; pull in to --space-1. */
		margin-inline-start: calc(var(--space-1) - var(--space-2));
		border-radius: var(--radius-full);
		background-color: var(--status-warning-surface);
		padding: 0 var(--space-1);
		line-height: 1.4;
		color: var(--status-warning);
		font-weight: var(--weight-bold);
		font-size: var(--font-size-xs);
		letter-spacing: var(--tracking-caps);
		text-transform: uppercase;
	}

	.header {
		display: flex;
		flex-direction: column;
		border-bottom: 1px solid var(--border-color);
		background-color: var(--surface);
	}

	.bar {
		display: flex;
		padding-top: var(--space-4);
		min-height: 4.25rem;

		@media screen and (min-width: 1100px) {
			align-items: center;
			padding-top: 0;
			height: 4.25rem;
		}
	}

	nav {
		display: flex;
		flex-grow: 1;
		flex-direction: column;

		@media screen and (min-width: 1100px) {
			flex-direction: row;
		}

		ul {
			display: flex;
			flex-direction: row;
			justify-content: space-between;
			align-items: center;
			padding-right: 12%;
			padding-left: 4%;
			list-style: none;

			@media screen and (min-width: 1100px) {
				justify-content: flex-start;
				padding-right: 0;
				padding-left: var(--space-7);
			}
		}

		/* `nav` prefix keeps this above `nav ul` in specificity. */
		.navItems {
			display: none;
			flex-direction: column;
			align-items: flex-start;
			margin: 0 auto;
			padding: var(--space-4);

			&.active {
				display: flex;
			}

			> li:last-child {
				margin: var(--space-1) var(--space-5);
			}

			@media screen and (min-width: 1100px) {
				display: flex;
				flex-grow: 1;
				flex-direction: row;
				align-items: center;
				margin-left: var(--space-4);
				padding: 0;
			}
		}
	}

	.menuIcon {
		@media screen and (min-width: 1100px) {
			display: none;
		}
	}

	.logoContainer {
		width: 23.4rem;
		height: 2rem;

		:global(a) {
			color: inherit;
			text-decoration: none;
		}
	}

	.spacer {
		flex-grow: 1;
	}

	.banner {
		display: flex;
		justify-content: space-between;
		align-items: center;
		border-bottom: 1px solid var(--banner-warning-edge);
		background-color: var(--banner-warning);
		padding: 0 var(--space-1) 0 var(--space-6);
		color: var(--text-color);

		&.info {
			border-bottom-color: var(--banner-info-edge);
			background-color: var(--banner-info);
		}

		a {
			color: inherit;
		}
	}

	.bannerMessage {
		font-weight: var(--weight-semibold);
		font-size: var(--font-size-xs);
	}
</style>
